// W-n in comments are the review-warning ids of the committed plan,
// docs/superpowers/plans/2026-09-19-cubbo-2818-event-close-reconciliation-wms.md. Phase 6 ids are
// cited as "Phase 6 E-n" and live in docs/superpowers/plans/2026-09-22-cubbo-2819-w2w-signature-wms.md.
export const KINDS = ['write_off', 'match', 'handoff']
// The fourth execution phase is an event-level document, not a row kind: it has counters and a
// phase of its own but never a bay row, so it sits beside KINDS rather than inside it.
export const REMOVAL_KIND = 'removal'
export const PHASES = [...KINDS, REMOVAL_KIND]
// Reason codes that mean something different at freeze than at execution: a write-off blocked
// at plan time with `units_changed` was swept after the count, not after the freeze, and the
// operator reads that motive from `blocked_reasons.<code>` instead of `reasons.<code>`.
export const FREEZE_BLOCK_REASONS = ['units_changed']

export const hasReconciliation = event => event?.reconciliation_state != null
export const isPolling = event => ['computing', 'executing'].includes(event?.reconciliation_state)
// `frozen` is the narrow event's approval; `signed` is the store-wide event's accept. Both go
// through the same PUT /execute with the displayed fingerprint.
export const canExecute = event => ['frozen', 'signed'].includes(event?.reconciliation_state)
export const hasSignature = event => event?.signature != null
// The platform decides who may record a signature (settler authority, state); the pane only
// mirrors its flag, and only on the one state a signature can land on, so a stale flag on a
// cached header offers nothing.
export const canRecordSignature = event => event?.can_record_signature === true && event?.reconciliation_state === 'pending_approval'
// Fail closed: a header that merely omits `execution_approved_at` must not re-offer Cancelar
// during an execution, so the reconciliation state gates it as well.
const PAST_APPROVAL = ['executing', 'settled', 'closed', 'canceled']
export const canCancel = event =>
  event?.status === 'open' ||
  (['reconciling', 'pending_approval'].includes(event?.status) && event?.execution_approved_at == null && !PAST_APPROVAL.includes(event?.reconciliation_state))
// An older platform sends no reconciliation_state: the footer keeps the report-only close.
export const footerAction = event => {
  if (event?.status === 'open') return hasReconciliation(event) ? 'reconcile' : 'close_legacy'
  // The stuck-computing remedy is Conciliar again (the platform re-enqueues the worker, W-1).
  if (event?.status === 'reconciling' && event?.reconciliation_state === 'computing') return 'retry_reconcile'
  return null
}
export const isStalled = event => event?.stalled === true
// Every reconciliation state the platform emits (the lockstep test pins copy for each).
// `signed` is derived: status pending_approval with the signature stamped and no acceptance yet.
export const RECONCILIATION_STATES = ['open', 'computing', 'frozen', 'pending_approval', 'signed', 'executing', 'settled', 'closed', 'canceled']
// Lifecycle rank of a reconciliation state, for "which header is further along".
// The three terminal states are alternatives, not steps: none outranks another.
const rank = state => (['settled', 'closed', 'canceled'].includes(state) ? RECONCILIATION_STATES.indexOf('settled') : RECONCILIATION_STATES.indexOf(state))
// The Eventos list renders an event's status/state column from these; a transition landing on one
// of them is the only reconciliation hop worth refreshing the list for (W-5 + controller ruling).
// Not a lifecycle "terminal" set: pending_approval can still be approved.
export const LIST_REFRESH_STATES = ['settled', 'pending_approval', 'signed', 'closed', 'canceled']
// An older platform reports no reconciliation_state at all (no tab); an event still open has
// nothing to show yet. A canceled event shows nothing either (W-7) — unless it was signed first:
// the platform then keeps the proposal and the signature as an audit record, read-only.
export const showsReconciliationTab = event => hasReconciliation(event) && event.status !== 'open' && (event.status !== 'canceled' || hasSignature(event))
// A mutation response carries the header only: fields it omits (id, execution_approved_at,
// store_wide) keep their last known value, so the gates and the title never read undefined.
export const mergeEventHeader = (previous, next) => (next ? { ...previous, ...next } : previous)
// The header the tab renders and polls from. Cached pages carry the header as it was when THEY
// were fetched, so a revisited page must not roll the state back: the newest header observed by
// this pane wins over the current page's cached one, and the parent's header wins when it says
// the execution is already past approval (fail closed on Aprobar and Cancelar).
export const pickHeader = ({ latest, cached, parent }) => {
  const base = latest ?? cached ?? parent ?? null
  // The parent's header wins when it is further along the lifecycle than what the pane holds
  // (executing over a cached frozen or pending_approval, settled over executing…): the gates
  // fail closed and the poll follows the real state. A signature captured meanwhile (signed after
  // pending_approval) ranks higher too, so a parent that already read it wins and the pane offers
  // the accept on a header that carries it.
  if (parent && base && rank(parent.reconciliation_state) > rank(base.reconciliation_state)) return mergeEventHeader(base, parent)
  return base
}
// Whether header `a` is strictly further along the lifecycle than header `b`. A mutation response
// that lands after the pane already observed a later state (a poll saw `executing` while a
// record-signature was in flight) is stale by this test: adopting it would roll the pane back.
export const furtherAlong = (a, b) => rank(a?.reconciliation_state) > rank(b?.reconciliation_state)

// Every reason code the platform can attach to a proposal item (the lockstep test pins copy for each).
export const REASON_CODES = [
  'insufficient_candidates',
  'mid_picking',
  'container_bound',
  'units_changed',
  'insufficient_lost_candidates',
  'bay_retired',
  'location_not_eligible_for_found_stock',
  'found_stock_disabled',
  'replenishment_invoice_required',
  'billing_config_missing',
  'unit_code_product',
  'product_missing',
  'store_missing',
  'concurrent_modification',
  'no_lost_bay',
  'error',
  'settlement_authority_lost',
  'multiple_stores',
]

// Poll policy for GET /reconciliation, kept pure so it can be pinned without a render.
export const POLL_MS = 4000
// A stalled run still polls (recovery has to be noticed) but at a quarter of the cadence (W-2).
export const STALLED_POLL_FACTOR = 4
// A parked proposal moves only when the merchant signs on the portal or a colleague records the
// signature elsewhere: neither reaches this tab on its own. A slow read (32 s at POLL_MS) keeps
// an operator who stays on the page honest without hammering the platform (Phase 6 eng review E-6).
export const PENDING_SIGNATURE_POLL_FACTOR = 8
export const isAwaitingMerchant = event => ['pending_approval', 'signed'].includes(event?.reconciliation_state)
// Retries per fetch before the failure reaches the operator; a 401 or 404 never retries (W-6).
export const MAX_RETRIES = 2
// Consecutive failed fetches after which the poll parks and the visible Reintentar is the way
// back: without a ceiling a failing route is hit every poll, times the retries, for as long as
// the tab is open. A 401 parks at once — every 401 also costs a refresh-token exchange.
export const MAX_CONSECUTIVE_FAILURES = 3
export const isParked = (error, failures) => error?.response?.status === 401 || failures >= MAX_CONSECUTIVE_FAILURES
export const shouldRetry = (count, error) => ![401, 404].includes(error?.response?.status) && count < MAX_RETRIES
const TERMINAL = ['settled', 'closed', 'canceled']
// `header` is the pane's OWN header (its last payload); `parent` the panel's. The cadence follows
// the pane's own state, not the parent's: a parent that already says settled must not stop the
// reads before the pane's payload carries the final rows. While the own header lags a parent
// that is further along (an approval seen elsewhere, an execution that finished), keep reading
// until it catches up — unless the own header is already terminal.
export const pollInterval = ({ header, parent = null, error, failures, pollMs }) => {
  if (isParked(error, failures)) return false
  const own = header ?? parent
  if (!own) return false
  if (isPolling(own)) return isStalled(own) ? pollMs * STALLED_POLL_FACTOR : pollMs
  if (isAwaitingMerchant(own) && !(parent && rank(parent.reconciliation_state) > rank(own.reconciliation_state))) return pollMs * PENDING_SIGNATURE_POLL_FACTOR
  if (parent && !TERMINAL.includes(own.reconciliation_state) && rank(parent.reconciliation_state) > rank(own.reconciliation_state)) return pollMs
  return false
}
export const OUTCOMES = ['pending', 'executed', 'partial', 'skipped', 'blocked']
export const displayOutcome = item => (item?.blocked ? 'blocked' : (item?.outcome ?? 'pending'))
export const currentPhase = counters => PHASES.find(kind => (counters?.items?.[kind]?.pending ?? 0) > 0) ?? null
